import * as React from 'react';
import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import CircularProgress from '@mui/joy/CircularProgress';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Stack from '@mui/joy/Stack';
import Table from '@mui/joy/Table';
import Checkbox from '@mui/joy/Checkbox';
import IconButton from '@mui/joy/IconButton';
import Button from '@mui/joy/Button';
import Tooltip from '@mui/joy/Tooltip';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

import ConfirmDialog from "../../components/confirmDialog/ConfirmDialog";
import { NotificationService } from "../../common/notification";
import { formatDateTime } from "../../common/dateTime";
import { Game, GameInventorySetting, InventorySyncTypeDescription } from "../../models/games";
import { gamesListApi, inventorySettingsList, inventorySettingsUpdate } from "./gamesApi";

const sortSettings = (list: GameInventorySetting[]) =>
    list.slice().sort((a, b) => a.sortOrder - b.sortOrder);

// Порядок применения определяется позицией строки в списке
const renumber = (list: GameInventorySetting[]) =>
    list.map((s, index) => ({ ...s, sortOrder: index + 1 }));

export function GameInventoryTab() {
    const [loadingGames, setLoadingGames] = React.useState(false);
    const [loadingSettings, setLoadingSettings] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [games, setGames] = React.useState<Game[]>([]);
    const [selectedGameId, setSelectedGameId] = React.useState<number | null>(null);
    const [savedSettings, setSavedSettings] = React.useState<GameInventorySetting[]>([]);
    const [settings, setSettings] = React.useState<GameInventorySetting[]>([]);
    // Игра, на которую пользователь пытается переключиться при наличии несохраненных изменений
    const [pendingGameId, setPendingGameId] = React.useState<number | null>(null);

    React.useEffect(() => {
        loadGames().catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (!selectedGameId) {
            applySettings([]);
            return;
        }
        loadSettings(selectedGameId).catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGameId]);

    const applySettings = (list: GameInventorySetting[]) => {
        const sorted = sortSettings(list);
        setSavedSettings(sorted);
        setSettings(sorted);
    };

    const loadGames = async () => {
        setLoadingGames(true);
        try {
            const res = await gamesListApi();
            if (!res.result) {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки");
                return;
            }
            const list = (res.value ?? []).slice().sort((a, b) => a.name.localeCompare(b.name));
            setGames(list);
            if (list.length > 0) {
                // По умолчанию выбираем TF2 (id = 440), если есть, иначе первую игру
                const preferred = list.find(g => g.id === 440) ?? list[0];
                setSelectedGameId(prev => prev ?? preferred.id);
            }
        } finally {
            setLoadingGames(false);
        }
    };

    const loadSettings = async (gameId: number) => {
        setLoadingSettings(true);
        try {
            const res = await inventorySettingsList(gameId);
            if (!res.result) {
                NotificationService.alertError("Пожалуйста попробуйте позже или обратитесь в службу поддержки");
                applySettings([]);
                return;
            }
            applySettings(res.value ?? []);
        } finally {
            setLoadingSettings(false);
        }
    };

    const moveRow = (index: number, direction: -1 | 1) => {
        const target = index + direction;
        if (target < 0 || target >= settings.length) return;
        const list = settings.slice();
        [list[index], list[target]] = [list[target], list[index]];
        setSettings(renumber(list));
    };

    const setEnabled = (index: number, enabled: boolean) => {
        setSettings(prev => prev.map((s, i) => i === index ? { ...s, enabled } : s));
    };

    const isDirty = React.useMemo(
        () => settings.length !== savedSettings.length || settings.some((s, i) =>
            s.syncType !== savedSettings[i].syncType
            || s.enabled !== savedSettings[i].enabled
            || s.sortOrder !== savedSettings[i].sortOrder),
        [settings, savedSettings],
    );

    const save = async () => {
        if (!selectedGameId) return;
        setSaving(true);
        try {
            const items = renumber(settings).map(s => ({ syncType: s.syncType, enabled: s.enabled, sortOrder: s.sortOrder }));
            const res = await inventorySettingsUpdate(selectedGameId, items);
            if (!res.result) {
                NotificationService.alertError("Не удалось сохранить настройки. Попробуйте позже.");
                return;
            }
            NotificationService.alertSuccess("Настройки сохранены");
            applySettings(res.value ?? []);
        } finally {
            setSaving(false);
        }
    };

    const changeGame = (gameId: number | null) => {
        if (gameId === selectedGameId) return;
        if (isDirty) {
            setPendingGameId(gameId);
            return;
        }
        setSelectedGameId(gameId);
    };

    const pageLoading = loadingGames || loadingSettings || saving;

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <ConfirmDialog
                title="Несохраненные изменения"
                open={pendingGameId !== null}
                setOpen={(open) => { if (!open) setPendingGameId(null); }}
                onConfirm={() => setSelectedGameId(pendingGameId)}
            >
                Изменения настроек инвентаря для текущей игры не сохранены и будут потеряны. Переключить игру?
            </ConfirmDialog>
            <Sheet variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { sm: 'flex-end' } }}>
                    <FormControl size="sm" sx={{ minWidth: 260, flex: 1 }}>
                        <FormLabel>Игра</FormLabel>
                        <Select
                            value={selectedGameId}
                            onChange={(_, v) => changeGame((v as number) ?? null)}
                            placeholder={loadingGames ? 'Загрузка...' : 'Выберите игру'}
                            disabled={saving}
                        >
                            {games.map(g => (
                                <Option key={g.id} value={g.id}>
                                    {g.name} ({g.id})
                                </Option>
                            ))}
                        </Select>
                    </FormControl>
                    {pageLoading && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size="sm" />
                            <Typography level="body-sm">Загрузка</Typography>
                        </Box>
                    )}
                </Stack>
            </Sheet>

            {!selectedGameId ? (
                <Sheet variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                    <Typography level="body-sm">Выберите игру, чтобы загрузить настройки инвентаря.</Typography>
                </Sheet>
            ) : loadingSettings ? (
                <Sheet variant="outlined" sx={{ p: 3, borderRadius: '8px', display: 'flex', justifyContent: 'center' }}>
                    <CircularProgress />
                </Sheet>
            ) : settings.length === 0 ? (
                <Sheet variant="outlined" sx={{ p: 2, borderRadius: '8px' }}>
                    <Typography level="body-sm">Настройки не найдены.</Typography>
                </Sheet>
            ) : (
                <Sheet variant="outlined" sx={{ p: 2, borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography level="body-sm">
                        Способы загрузки инвентаря применяются сверху вниз. Отключенные способы не используются.
                    </Typography>
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="sm" sx={{ '--TableCell-paddingX': '8px', minWidth: 420 }}>
                            <thead>
                            <tr>
                                <th style={{ width: 60 }}>Порядок</th>
                                <th>Способ загрузки</th>
                                <th style={{ width: 90 }}>Включен</th>
                                <th style={{ width: 150 }}>Изменен</th>
                                <th style={{ width: 90 }}></th>
                            </tr>
                            </thead>
                            <tbody>
                            {settings.map((s, index) => (
                                <tr key={s.syncType}>
                                    <td>
                                        <Typography level="body-xs">{s.sortOrder}</Typography>
                                    </td>
                                    <td>
                                        <Typography level="body-sm">{InventorySyncTypeDescription[s.syncType] ?? s.syncType}</Typography>
                                    </td>
                                    <td>
                                        <Checkbox size="sm"
                                                  checked={s.enabled}
                                                  disabled={saving}
                                                  onChange={(event) => setEnabled(index, event.target.checked)}/>
                                    </td>
                                    <td>
                                        <Typography level="body-xs">
                                            {s.updated ? formatDateTime(s.updated) : 'по умолчанию'}
                                        </Typography>
                                    </td>
                                    <td>
                                        <Stack direction="row" spacing={0.5}>
                                            <Tooltip title="Выше">
                                                <span>
                                                    <IconButton size="sm" variant="outlined"
                                                                aria-label="Переместить выше"
                                                                disabled={saving || index === 0}
                                                                onClick={() => moveRow(index, -1)}>
                                                        <ArrowUpwardIcon/>
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Ниже">
                                                <span>
                                                    <IconButton size="sm" variant="outlined"
                                                                aria-label="Переместить ниже"
                                                                disabled={saving || index === settings.length - 1}
                                                                onClick={() => moveRow(index, 1)}>
                                                        <ArrowDownwardIcon/>
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Stack>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </Table>
                    </Box>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="sm" variant="plain" color="neutral"
                                disabled={!isDirty || saving}
                                onClick={() => setSettings(savedSettings)}>
                            Отменить изменения
                        </Button>
                        <Button size="sm" color="primary"
                                loading={saving}
                                disabled={!isDirty}
                                onClick={save}>
                            Сохранить
                        </Button>
                    </Stack>
                </Sheet>
            )}
        </Box>
    );
}
